import { useMemo, type ReactElement } from 'react';
import { Link } from 'react-router';
import { messageForError } from '@proctoring/api-client';
import type { Interview } from '@proctoring/shared';
import { useInterviewerInterviews } from '../../api/queries.js';
import { FullPageMessage } from '../../components/FullPageMessage.js';
import { StateBadge } from '../../components/StateBadge.js';
import { formatDateTime } from '../../components/format.js';

/**
 * Splits the single list the API returns into the two the dashboard shows.
 *
 * "Past" is keyed on lifecycle state rather than the clock: an interview scheduled for this
 * morning that never ran is still upcoming (the room stays joinable — design §5), and only
 * `completed`/`cancelled` genuinely belong in the history.
 */
function partition(interviews: Interview[]): { upcoming: Interview[]; past: Interview[] } {
  const upcoming: Interview[] = [];
  const past: Interview[] = [];
  for (const interview of interviews) {
    if (interview.state === 'completed' || interview.state === 'cancelled') past.push(interview);
    else upcoming.push(interview);
  }
  upcoming.sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt));
  past.sort((a, b) => b.scheduledAt.localeCompare(a.scheduledAt));
  return { upcoming, past };
}

function InterviewRow({ interview }: { interview: Interview }): ReactElement {
  return (
    <li data-testid="interview-row" className="border-b border-session-border last:border-b-0">
      <Link
        to={`/interviewer/interviews/${interview.id}`}
        className="flex items-center justify-between gap-4 px-5 py-4 transition-colors hover:bg-session-panel-raised"
      >
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-session-text">
            {interview.jobProfile.name}
          </p>
          <p className="mt-0.5 text-sm text-session-muted">
            {formatDateTime(interview.scheduledAt)}
          </p>
        </div>
        <StateBadge state={interview.state} />
      </Link>
    </li>
  );
}

function Section({
  heading,
  interviews,
  emptyMessage,
}: {
  heading: string;
  interviews: Interview[];
  emptyMessage: string;
}): ReactElement {
  const id = heading.toLowerCase().replace(/\s+/g, '-');
  return (
    <section className="mt-8" aria-labelledby={id}>
      <h2 id={id} className="mb-3 text-sm font-semibold text-session-text">
        {heading}
      </h2>
      {interviews.length === 0 ? (
        <p className="panel px-5 py-6 text-sm text-session-muted">{emptyMessage}</p>
      ) : (
        <ul className="panel overflow-hidden">
          {interviews.map((interview) => (
            <InterviewRow key={interview.id} interview={interview} />
          ))}
        </ul>
      )}
    </section>
  );
}

export function InterviewerDashboardPage(): ReactElement {
  const { data, isPending, error } = useInterviewerInterviews();
  const { upcoming, past } = useMemo(() => partition(data ?? []), [data]);

  if (isPending) return <FullPageMessage title="Loading your interviews…" />;
  if (error) {
    return <FullPageMessage title="Could not load interviews" detail={messageForError(error)} />;
  }

  return (
    <div className="mx-auto w-full max-w-4xl px-6 py-10">
      <h1 className="text-xl font-semibold tracking-tight text-session-text">Interviews</h1>
      <p className="mt-1 text-sm text-session-muted">
        Every interview you have been invited to, newest history first.
      </p>

      <Section
        heading="Upcoming"
        interviews={upcoming}
        emptyMessage="You have no upcoming interviews."
      />
      <Section heading="Past" interviews={past} emptyMessage="No past interviews yet." />
    </div>
  );
}
